import React, { useState, useEffect } from "react";
import {
  getSources,
  createSource,
  deleteSource,
  getSourceStats,
} from "../lib/api";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { formatDate } from "../lib/utils";
import {
  Plus,
  MoreHorizontal,
  Trash2,
  Package,
  Users,
  BarChart3,
  TrendingUp,
  Target,
  Megaphone,
  UserCheck,
  Loader2,
  Calendar,
} from "lucide-react";

export const SourcesPage = () => {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sourceToDelete, setSourceToDelete] = useState(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedStats, setSelectedStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
  });

  const fetchSources = async () => {
    try {
      const response = await getSources();
      setSources(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des sources");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, []);

  const handleOpenForm = () => {
    setFormData({ name: "" });
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormData({ name: "" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Le nom de la source est requis");
      return;
    }

    try {
      await createSource(formData);
      toast.success("Source créée avec succès");
      handleCloseForm();
      fetchSources();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    }
  };

  const handleDelete = async () => {
    if (!sourceToDelete) return;

    try {
      await deleteSource(sourceToDelete.id);
      toast.success("Source supprimée avec succès");
      setDeleteDialogOpen(false);
      setSourceToDelete(null);
      fetchSources();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Erreur lors de la suppression"
      );
    }
  };

  const handleViewStats = async (source) => {
    setLoadingStats(true);
    setStatsDialogOpen(true);
    try {
      const response = await getSourceStats(source.id);
      setSelectedStats(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des statistiques");
      setStatsDialogOpen(false);
    } finally {
      setLoadingStats(false);
    }
  };

  // Calculate totals
  const totalLeads = sources.reduce((sum, s) => sum + (s.leadCount || 0), 0);
  const totalSources = sources.length;
  const avgLeadsPerSource = totalSources > 0 ? Math.round(totalLeads / totalSources) : 0;
  const topSource = sources.length > 0 
    ? sources.reduce((max, s) => (s.leadCount || 0) > (max.leadCount || 0) ? s : max, sources[0])
    : null;

  if (loading) {
    return (
      <div className="space-y-6" data-testid="sources-page-loading">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64 mt-2" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sources-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Sources</h1>
          <p className="text-muted-foreground mt-1">
            Gérez vos fournisseurs de leads et analysez leurs performances
          </p>
        </div>
        <Button onClick={handleOpenForm} data-testid="add-source-button">
          <Plus className="h-4 w-4 mr-2" />
          Nouvelle source
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total sources</p>
                <p className="text-3xl font-bold">{totalSources}</p>
              </div>
              <Package className="h-10 w-10 text-primary/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total leads</p>
                <p className="text-3xl font-bold">{totalLeads}</p>
              </div>
              <Users className="h-10 w-10 text-blue-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Moy. par source</p>
                <p className="text-3xl font-bold">{avgLeadsPerSource}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-500/20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Top source</p>
                <p className="text-xl font-bold truncate max-w-[120px]" title={topSource?.name}>
                  {topSource?.name || "-"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {topSource?.leadCount || 0} leads
                </p>
              </div>
              <Target className="h-10 w-10 text-orange-500/20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Sources Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Liste des sources (fournisseurs)</CardTitle>
        </CardHeader>
        <CardContent>
          {sources.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="font-medium">Aucune source créée</p>
              <p className="text-sm mt-1">
                Créez votre première source pour catégoriser la provenance de vos leads
              </p>
              <Button onClick={handleOpenForm} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Créer une source
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sources.map((source) => (
                <Card key={source.id} className="hover:shadow-md transition-shadow border-l-4 border-l-primary">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Package className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{source.name}</CardTitle>
                          <p className="text-xs text-muted-foreground mt-1">
                            Créée le {formatDate(source.createdAt)}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleViewStats(source)}>
                            <BarChart3 className="h-4 w-4 mr-2" />
                            Statistiques
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => {
                              setSourceToDelete(source);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{source.leadCount || 0}</span>
                        <span className="text-muted-foreground">leads</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleViewStats(source)}
                        className="text-xs"
                      >
                        <BarChart3 className="h-3 w-3 mr-1" />
                        Voir stats
                      </Button>
                    </div>
                    {/* Mini progress bar showing proportion of total leads */}
                    {totalLeads > 0 && (
                      <div className="mt-3">
                        <div className="flex justify-between text-xs text-muted-foreground mb-1">
                          <span>Part du total</span>
                          <span>{Math.round(((source.leadCount || 0) / totalLeads) * 100)}%</span>
                        </div>
                        <Progress 
                          value={((source.leadCount || 0) / totalLeads) * 100} 
                          className="h-1.5"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Source Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle source</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la source</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Ex: Facebook Ads, Google Ads, Salon Pro..."
                data-testid="source-form-name"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button type="submit" data-testid="source-form-submit">
                Créer
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette source ?</AlertDialogTitle>
            <AlertDialogDescription>
              {sourceToDelete?.leadCount > 0
                ? `Cette source a ${sourceToDelete.leadCount} leads associés. Vous devez d'abord réassigner ou supprimer ces leads.`
                : "Cette action est irréversible. La source sera définitivement supprimée."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={sourceToDelete?.leadCount > 0}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stats Dialog */}
      <Dialog open={statsDialogOpen} onOpenChange={setStatsDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Statistiques de la source
            </DialogTitle>
          </DialogHeader>
          
          {loadingStats ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : selectedStats && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Package className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{selectedStats.source.name}</h3>
                  <p className="text-sm text-muted-foreground">
                    {selectedStats.totalLeads} leads au total
                  </p>
                </div>
              </div>

              {/* Key Metrics */}
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4 text-center">
                    <Users className="h-6 w-6 mx-auto text-blue-500 mb-2" />
                    <p className="text-2xl font-bold">{selectedStats.totalLeads}</p>
                    <p className="text-xs text-muted-foreground">Total leads</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <Target className="h-6 w-6 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold">{selectedStats.convertedLeads}</p>
                    <p className="text-xs text-muted-foreground">Convertis</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <TrendingUp className="h-6 w-6 mx-auto text-orange-500 mb-2" />
                    <p className="text-2xl font-bold">{selectedStats.conversionRate}%</p>
                    <p className="text-xs text-muted-foreground">Taux conversion</p>
                  </CardContent>
                </Card>
              </div>

              {/* Leads by Status */}
              {selectedStats.leadsByStatus?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Répartition par statut
                  </h4>
                  <div className="space-y-2">
                    {selectedStats.leadsByStatus.map((status) => (
                      <div
                        key={status.statusId}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: status.statusColor }}
                          />
                          <span className="text-sm">{status.statusName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary">{status.count}</Badge>
                          <span className="text-xs text-muted-foreground">
                            ({Math.round((status.count / selectedStats.totalLeads) * 100)}%)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leads by Campaign */}
              {selectedStats.leadsByCampaign?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Megaphone className="h-4 w-4" />
                    Leads par campagne
                  </h4>
                  <div className="space-y-2">
                    {selectedStats.leadsByCampaign.slice(0, 5).map((campaign) => (
                      <div
                        key={campaign.campaignId}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: campaign.campaignColor }}
                          />
                          <span className="text-sm">{campaign.campaignName}</span>
                        </div>
                        <Badge variant="secondary">{campaign.count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leads by Seller */}
              {selectedStats.leadsBySeller?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <UserCheck className="h-4 w-4" />
                    Leads par vendeur
                  </h4>
                  <div className="space-y-2">
                    {selectedStats.leadsBySeller.slice(0, 5).map((seller) => (
                      <div
                        key={seller.sellerId}
                        className="flex items-center justify-between p-3 rounded-lg border"
                      >
                        <span className="text-sm">{seller.sellerName}</span>
                        <Badge variant="secondary">{seller.count}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Leads Over Time */}
              {selectedStats.leadsOverTime?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Leads sur les 30 derniers jours
                  </h4>
                  <div className="p-3 rounded-lg border">
                    <div className="flex items-end gap-1 h-20">
                      {selectedStats.leadsOverTime.map((day, index) => {
                        const maxCount = Math.max(...selectedStats.leadsOverTime.map(d => d.count));
                        const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                        return (
                          <div
                            key={index}
                            className="flex-1 bg-primary rounded-t transition-all hover:bg-primary/80"
                            style={{ height: `${Math.max(height, 5)}%` }}
                            title={`${day.date}: ${day.count} leads`}
                          />
                        );
                      })}
                    </div>
                    <div className="flex justify-between mt-2 text-xs text-muted-foreground">
                      <span>{selectedStats.leadsOverTime[0]?.date}</span>
                      <span>{selectedStats.leadsOverTime[selectedStats.leadsOverTime.length - 1]?.date}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {selectedStats.totalLeads === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun lead dans cette source</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
